import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Hash, Target, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import MainLayout from "@/components/MainLayout";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Lotteries() {
  const navigate = useNavigate();
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [selectedLottery, setSelectedLottery] = useState(null);
  const [checkingFeed, setCheckingFeed] = useState(null);
  const [checkHistory, setCheckHistory] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    numbers_count: 6,
    min_number: 0,
    max_number: 99,
  });
  const [feedFormData, setFeedFormData] = useState({
    feed_url: "",
    schedule_enabled: false,
    schedule_day: "Monday",
    schedule_time: "11:00",
  });
  const [resultNumbers, setResultNumbers] = useState([]);
  const [resultText, setResultText] = useState("");
  const [pendingByLottery, setPendingByLottery] = useState({});

  useEffect(() => {
    loadLotteries();
    loadPendingEmails();
  }, []);

  const loadPendingEmails = async () => {
    try {
      const res = await axios.get(`${API}/email-queue/pending-by-lottery`);
      setPendingByLottery(res.data);
    } catch (error) {
      toast.error("Failed to load pending emails");
    }
  };

  const loadLotteries = async () => {
    try {
      const response = await axios.get(`${API}/lotteries`);
      setLotteries(response.data);
    } catch (error) {
      toast.error("Failed to load lotteries");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/lotteries`, formData);
      toast.success("Lottery created successfully");
      setCreateDialogOpen(false);
      setFormData({ name: "", numbers_count: 6, min_number: 0, max_number: 99 });
      loadLotteries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create lottery");
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/lotteries/${selectedLottery.id}`, {
        name: formData.name,
        numbers_count: formData.numbers_count,
        min_number: formData.min_number,
        max_number: formData.max_number,
      });
      toast.success("Lottery updated successfully");
      setEditDialogOpen(false);
      loadLotteries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update lottery");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/lotteries/${selectedLottery.id}`);
      toast.success("Lottery deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedLottery(null);
      loadLotteries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete lottery");
    }
  };

  const handleUpdateResult = async () => {
    try {
      await axios.put(`${API}/lotteries/${selectedLottery.id}/result`, {
        numbers: resultNumbers,
        result_text: resultText,
      });
      const message = resultText 
        ? "Lottery result and text updated for all banners" 
        : "Lottery result updated successfully";
      toast.success(message);
      setResultDialogOpen(false);
      setResultText("");
      loadLotteries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update result");
    }
  };

  const handleUpdateFeedSettings = async () => {
    try {
      await axios.put(`${API}/lotteries/${selectedLottery.id}`, feedFormData);
      toast.success("Feed settings updated successfully");
      setFeedDialogOpen(false);
      loadLotteries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update feed settings");
    }
  };

  const handleCheckFeedNow = async (lottery) => {
    setCheckingFeed(lottery.id);
    try {
      const response = await axios.post(`${API}/lotteries/${lottery.id}/check-feed`);
      const result = response.data;
      
      if (result.status === "updated") {
        toast.success(result.message);
      } else if (result.status === "unchanged") {
        toast.info(result.message);
      } else {
        toast.error(result.message || "Feed check failed");
      }
      loadLotteries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check feed");
    } finally {
      setCheckingFeed(null);
    }
  };

  const openEditDialog = (lottery) => {
    setSelectedLottery(lottery);
    setFormData({
      name: lottery.name,
      numbers_count: lottery.numbers_count,
      min_number: lottery.min_number,
      max_number: lottery.max_number,
    });
    setEditDialogOpen(true);
  };

  const openResultDialog = (lottery) => {
    setSelectedLottery(lottery);
    setResultNumbers([...lottery.current_result]);
    setResultText(lottery.result_text || "");
    setResultDialogOpen(true);
  };

  const openFeedDialog = (lottery) => {
    setSelectedLottery(lottery);
    setFeedFormData({
      feed_url: lottery.feed_url || "",
      schedule_enabled: lottery.schedule_enabled || false,
      schedule_day: lottery.schedule_day || "Monday",
      schedule_time: lottery.schedule_time || "11:00",
    });
    setCheckHistory([]);
    setFeedDialogOpen(true);
    // Load check history
    axios.get(`${API}/lotteries/${lottery.id}/check-history?limit=10`)
      .then(res => setCheckHistory(res.data.history || []))
      .catch(() => {});
  };

  const openDeleteDialog = (lottery) => {
    setSelectedLottery(lottery);
    setDeleteDialogOpen(true);
  };

  const getCheckStatusIcon = (lottery) => {
    if (!lottery.last_check_result) return null;
    switch (lottery.last_check_result) {
      case "updated":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "unchanged":
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case "error":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <MainLayout title="Lotteries" subtitle="Manage your lottery configurations">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
        </div>
      </MainLayout>
    );
  }

  const headerActions = (
    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#10B981] hover:bg-[#059669]" data-testid="create-lottery-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Lottery
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F172A] border-[#1E293B] text-white">
        <DialogHeader>
          <DialogTitle>Create New Lottery</DialogTitle>
          <DialogDescription className="text-gray-400">
            Set up a new lottery with custom number configuration
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Lottery Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Powerball, Mega Millions"
              className="bg-[#1E293B] border-[#334155]"
              data-testid="lottery-name-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Numbers Count</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={formData.numbers_count}
                onChange={(e) => setFormData({ ...formData, numbers_count: parseInt(e.target.value) || 1 })}
                className="bg-[#1E293B] border-[#334155]"
                data-testid="numbers-count-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Min Number</Label>
              <Input
                type="number"
                min={0}
                value={formData.min_number}
                onChange={(e) => setFormData({ ...formData, min_number: parseInt(e.target.value) || 0 })}
                className="bg-[#1E293B] border-[#334155]"
                data-testid="min-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Number</Label>
              <Input
                type="number"
                min={0}
                value={formData.max_number}
                onChange={(e) => setFormData({ ...formData, max_number: parseInt(e.target.value) || 99 })}
                className="bg-[#1E293B] border-[#334155]"
                data-testid="max-number-input"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-[#10B981] hover:bg-[#059669]"
            disabled={!formData.name}
            data-testid="create-lottery-submit"
          >
            Create Lottery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <MainLayout title="Lotteries" subtitle="Manage your lottery configurations" headerActions={headerActions}>
      {lotteries.length === 0 ? (
        <div className="text-center py-16">
          <Hash className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <h2 className="text-xl font-semibold text-gray-400 mb-2">No Lotteries Yet</h2>
          <p className="text-gray-500 mb-4">Create your first lottery to get started</p>
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#10B981] hover:bg-[#059669]">
            <Plus className="w-4 h-4 mr-2" />
            Create Lottery
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lotteries.map((lottery) => (
            <Card key={lottery.id} className="bg-[#0F172A] border-[#1E293B]" data-testid={`lottery-card-${lottery.id}`}>
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>{lottery.name}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openFeedDialog(lottery)}
                      className={lottery.feed_url ? "text-[#10B981] hover:text-[#059669]" : "text-gray-400 hover:text-white"}
                      title="Feed Settings"
                      data-testid={`feed-settings-${lottery.id}`}
                    >
                      <Clock className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(lottery)}
                      className="text-gray-400 hover:text-white"
                      data-testid={`edit-lottery-${lottery.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(lottery)}
                      className="text-gray-400 hover:text-red-500"
                      data-testid={`delete-lottery-${lottery.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {lottery.numbers_count} numbers • Range: {lottery.min_number}-{lottery.max_number}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400 text-sm">Current Result</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {lottery.current_result.map((num, idx) => (
                        <span
                          key={`result-${idx}-${num}`}
                          className="w-10 h-10 flex items-center justify-center bg-[#10B981] text-white rounded-full font-bold"
                        >
                          {num}
                        </span>
                      ))}
                    </div>
                    {lottery.result_text && (
                      <p className="text-sm text-gray-400 mt-2 italic">"{lottery.result_text}"</p>
                    )}
                  </div>

                  {/* Feed Status */}
                  {lottery.feed_url && (
                    <div className="p-3 bg-[#1E293B] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400">Feed Status</span>
                        <div className="flex items-center gap-2">
                          {getCheckStatusIcon(lottery)}
                          <span className="text-xs text-gray-400">
                            {lottery.schedule_enabled ? `${lottery.schedule_day} ${lottery.schedule_time} GMT` : "Manual only"}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        Last check: {formatDateTime(lottery.last_check_at)}
                        {lottery.last_draw_id && (
                          <span className="ml-2">• Draw #{lottery.last_draw_id}</span>
                        )}
                      </div>
                      {lottery.last_check_error && (
                        <p className="text-xs text-red-400 mt-1">{lottery.last_check_error}</p>
                      )}
                    </div>
                  )}

                  {/* Email Delivery Controls */}
                  <div className="p-3 bg-[#1E293B] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">Email Delivery</span>
                      <Switch
                        checked={lottery.email_enabled || false}
                        onCheckedChange={async (checked) => {
                          try {
                            await axios.put(`${API}/lotteries/${lottery.id}`, { email_enabled: checked });
                            loadLotteries();
                            toast.success(checked ? "Email delivery enabled" : "Email delivery disabled");
                          } catch { toast.error("Failed to update"); }
                        }}
                        data-testid={`email-toggle-${lottery.id}`}
                      />
                    </div>
                    {lottery.email_enabled && (
                      <div className="mt-2">
                        <label className="text-xs text-gray-500 block mb-1">Send delay</label>
                        <select
                          value={lottery.email_delay_minutes || 0}
                          onChange={async (e) => {
                            try {
                              await axios.put(`${API}/lotteries/${lottery.id}`, { email_delay_minutes: parseInt(e.target.value) });
                              loadLotteries();
                            } catch { toast.error("Failed to update delay"); }
                          }}
                          className="w-full h-8 px-2 rounded bg-[#0F172A] border border-white/10 text-xs text-white"
                          data-testid={`email-delay-${lottery.id}`}
                        >
                          <option value="0">Immediately</option>
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="60">1 hour</option>
                          <option value="120">2 hours</option>
                          <option value="180">3 hours</option>
                          <option value="240">4 hours</option>
                          <option value="300">5 hours</option>
                        </select>
                      </div>
                    )}
                    {/* Pending email indicator */}
                    {pendingByLottery[lottery.id] && (
                      <div className="mt-2 bg-amber-500/10 border border-amber-500/20 rounded-md p-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-amber-400" />
                          <span className="text-xs text-amber-300">
                            {pendingByLottery[lottery.id].count} email{pendingByLottery[lottery.id].count !== 1 ? 's' : ''} pending
                          </span>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/email-queue/cancel-lottery/${lottery.id}`);
                              toast.success("Pending emails cancelled");
                              loadPendingEmails();
                            } catch { toast.error("Failed to cancel"); }
                          }}
                          className="text-xs text-red-400 hover:text-red-300 font-medium"
                          data-testid={`cancel-pending-${lottery.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {lottery.feed_url && (
                      <Button
                        onClick={() => handleCheckFeedNow(lottery)}
                        disabled={checkingFeed === lottery.id}
                        className="w-full bg-[#3B82F6] hover:bg-[#2563EB]"
                        data-testid={`check-feed-${lottery.id}`}
                      >
                        {checkingFeed === lottery.id ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Check Now
                      </Button>
                    )}
                    <Button
                      onClick={() => openResultDialog(lottery)}
                      className="w-full bg-[#1E293B] hover:bg-[#334155]"
                      data-testid={`update-result-${lottery.id}`}
                    >
                      <Target className="w-4 h-4 mr-2" />
                      Manual Update
                    </Button>
                    <Button
                      onClick={() => navigate(`/banner-editor?lottery=${lottery.id}`)}
                      variant="outline"
                      className="w-full border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
                      data-testid={`create-banner-${lottery.id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Banner
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-white">
          <DialogHeader>
            <DialogTitle>Edit Lottery</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update lottery configuration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Lottery Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#1E293B] border-[#334155]"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Numbers Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={formData.numbers_count}
                  onChange={(e) => setFormData({ ...formData, numbers_count: parseInt(e.target.value) || 1 })}
                  className="bg-[#1E293B] border-[#334155]"
                />
              </div>
              <div className="space-y-2">
                <Label>Min Number</Label>
                <Input
                  type="number"
                  min={0}
                  value={formData.min_number}
                  onChange={(e) => setFormData({ ...formData, min_number: parseInt(e.target.value) || 0 })}
                  className="bg-[#1E293B] border-[#334155]"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Number</Label>
                <Input
                  type="number"
                  min={0}
                  value={formData.max_number}
                  onChange={(e) => setFormData({ ...formData, max_number: parseInt(e.target.value) || 99 })}
                  className="bg-[#1E293B] border-[#334155]"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-[#10B981] hover:bg-[#059669]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feed Settings Dialog */}
      <Dialog open={feedDialogOpen} onOpenChange={setFeedDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-white">
          <DialogHeader>
            <DialogTitle>Feed Settings</DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure automatic result fetching for {selectedLottery?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Feed URL</Label>
              <Input
                value={feedFormData.feed_url}
                onChange={(e) => setFeedFormData({ ...feedFormData, feed_url: e.target.value })}
                placeholder="https://example.com/api/latest-draw/5"
                className="bg-[#1E293B] border-[#334155]"
                data-testid="feed-url-input"
              />
              <p className="text-xs text-gray-500">
                URL that returns JSON with draw_id, digits, and published_at
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Scheduled Checks</Label>
                <p className="text-xs text-gray-500">Automatically check for new results</p>
              </div>
              <Switch
                checked={feedFormData.schedule_enabled}
                onCheckedChange={(checked) => setFeedFormData({ ...feedFormData, schedule_enabled: checked })}
                data-testid="schedule-enabled-switch"
              />
            </div>

            {feedFormData.schedule_enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Day</Label>
                  <Select
                    value={feedFormData.schedule_day}
                    onValueChange={(value) => setFeedFormData({ ...feedFormData, schedule_day: value })}
                  >
                    <SelectTrigger className="bg-[#1E293B] border-[#334155]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155]">
                      {DAYS_OF_WEEK.map((day) => (
                        <SelectItem key={day} value={day}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Time (GMT)</Label>
                  <Input
                    type="time"
                    value={feedFormData.schedule_time}
                    onChange={(e) => setFeedFormData({ ...feedFormData, schedule_time: e.target.value })}
                    className="bg-[#1E293B] border-[#334155]"
                    data-testid="schedule-time-input"
                  />
                </div>
              </div>
            )}

            {feedFormData.schedule_enabled && (
              <p className="text-xs text-gray-500">
                At the scheduled time, the system will check every 5 minutes for up to 30 minutes until a new draw is found.
              </p>
            )}

            {/* Check History */}
            {checkHistory.length > 0 && (
              <div className="space-y-2">
                <Label className="text-gray-400">Recent Check History</Label>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {checkHistory.map((entry, idx) => (
                    <div key={entry.checked_at || `check-${idx}`} className="flex items-center gap-2 text-xs p-2 bg-[#1E293B] rounded" data-testid={`check-history-${idx}`}>
                      {entry.status === "updated" && <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />}
                      {entry.status === "unchanged" && <AlertCircle className="w-3 h-3 text-yellow-500 shrink-0" />}
                      {entry.status === "error" && <XCircle className="w-3 h-3 text-red-500 shrink-0" />}
                      <span className="text-gray-400 shrink-0">{formatDateTime(entry.checked_at)}</span>
                      <span className="text-gray-300 truncate">{entry.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateFeedSettings} className="bg-[#10B981] hover:bg-[#059669]">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={resultDialogOpen} onOpenChange={setResultDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-white">
          <DialogHeader>
            <DialogTitle>Update Lottery Result</DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedLottery?.name} - Enter the winning numbers
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex flex-wrap gap-3 justify-center">
              {resultNumbers.map((num, idx) => (
                <div key={`input-${idx}`} className="space-y-1">
                  <Label className="text-xs text-gray-400">#{idx + 1}</Label>
                  <Input
                    type="number"
                    min={selectedLottery?.min_number || 0}
                    max={selectedLottery?.max_number || 99}
                    value={num}
                    onChange={(e) => {
                      const newNumbers = [...resultNumbers];
                      newNumbers[idx] = parseInt(e.target.value) || 0;
                      setResultNumbers(newNumbers);
                    }}
                    className="w-16 bg-[#1E293B] border-[#334155] text-center"
                    data-testid={`result-number-${idx}`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Label className="text-gray-400">Result Text (optional)</Label>
              <Input
                value={resultText}
                onChange={(e) => setResultText(e.target.value)}
                placeholder="e.g., Latest Draw: 18th Feb 2026"
                className="mt-2 bg-[#1E293B] border-[#334155]"
                data-testid="result-text-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                This text will be applied as the "Result Text" on all banners for this lottery
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResultDialogOpen(false); setResultText(""); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateResult} className="bg-[#10B981] hover:bg-[#059669]">
              Update Result
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0F172A] border-[#1E293B]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Lottery?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete "{selectedLottery?.name}"? This action cannot be undone.
              Any banners using this lottery must be deleted or reassigned first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
